/**
 * Token refresh utilities
 *
 * Provides:
 * 1. attemptTokenRefresh() — shared, deduplicated refresh function used by
 *    the router guard, fetchUser, and the proactive timer.
 * 2. Proactive 12-min timer — keeps access tokens fresh during long SPA
 *    sessions so the router guard never sees an expired token.
 *
 * Extracted into its own module to avoid circular imports between main.ts,
 * vue-apollo.ts, and nexusAuth.ts.
 */
import { getRefreshToken, setRefreshToken } from '@/services/secureStorage/secureStorage';
import { onNexusLogin } from '@/apollo/nexusAuth';
import nexusProvider from '@/apollo/nexusProvider';
import { AuthRefreshMutation } from '@/generated/nexus-graphql';
import { REFRESH_MUTATION } from '@/graphql';

let refreshTimerId: ReturnType<typeof setInterval> | null = null;

// Deduplication: if a refresh is already in flight, concurrent callers
// piggyback on the same promise instead of firing parallel mutations.
let inflightRefresh: Promise<boolean> | null = null;

/**
 * Attempt a silent token refresh using the Nexus authRefresh mutation.
 * Returns true if a new access token was obtained, false otherwise.
 * Does NOT force logout on failure — callers decide what to do.
 */
export async function attemptTokenRefresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    if (!getRefreshToken()) return false;
    try {
      const { data } = await nexusProvider.defaultClient.mutate<AuthRefreshMutation>({
        mutation: REFRESH_MUTATION,
        fetchPolicy: 'no-cache'
      });
      if (data?.authRefresh?.ok && data.authRefresh.accessToken) {
        await onNexusLogin(data.authRefresh.accessToken);
        if (data.authRefresh.refreshToken) setRefreshToken(data.authRefresh.refreshToken);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })();

  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
}

export function startTokenRefreshTimer() {
  if (refreshTimerId) clearInterval(refreshTimerId);

  // Refresh every 12 minutes (3 min before the 15-min backend expiry)
  refreshTimerId = setInterval(async () => {
    const success = await attemptTokenRefresh();
    if (success) {
      console.log('🔄 Token refreshed proactively');
    }
  }, 12 * 60 * 1000); // 12 minutes
}

export function stopTokenRefreshTimer() {
  if (refreshTimerId) {
    clearInterval(refreshTimerId);
    refreshTimerId = null;
  }
}
